import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cardsAPI, walletAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, Loader2, X, Check, Eye, Lock } from 'lucide-react';
import axios from 'axios';
import { DesignTab } from '../components/card-editor/DesignTab';
import { BackgroundTab } from '../components/card-editor/BackgroundTab';
import { ColorsTab } from '../components/card-editor/ColorsTab';
import { RewardsTab } from '../components/card-editor/RewardsTab';
import { WalletTab } from '../components/card-editor/WalletTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'design', label: 'Identité', num: 1 },
  { id: 'background', label: 'Visuel', num: 2 },
  { id: 'colors', label: 'Couleurs', num: 3 },
  { id: 'rewards', label: 'Fidélité', num: 4 },
  { id: 'wallet', label: 'Wallet', num: 5, requiresSave: true },
];

// A banner is nice to have, never required: it must not block the next steps
// nor make the card look unfinished.
const OPTIONAL_TABS = new Set(['background']);

const isTabComplete = (tabId, formData) => {
  switch (tabId) {
    case 'design': return !!(formData.name && formData.name.trim().length > 0);
    case 'background': return !!(formData.strip_url || formData.strip_image_url || formData.background_url);
    case 'colors': return !!(formData.primary_color && formData.primary_color !== '');
    case 'rewards':
      if (formData.card_type === 'stamps') return (formData.total_stamps || 0) > 0;
      if (formData.card_type === 'points') return ((formData.points_per_euro ?? formData.points_per_visit) || 0) >= 1;
      return false;
    case 'wallet': return true;
    default: return false;
  }
};

const CardEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [walletTemplates, setWalletTemplates] = useState(null);
  const [activeTab, setActiveTab] = useState('design');
  const [saveStatus, setSaveStatus] = useState('idle');
  const saveTimerRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    primary_color: '#2563EB',
    secondary_color: '#FFFFFF',
    logo_url: '',
    background_url: '',
    card_type: 'points',
    points_per_visit: 1,
    points_per_euro: 1,
    reward_threshold: 10,
    total_stamps: 10,
    reward_description: '',
    stamp_icon: '',
    reward_label: '',
    reward_progress_text: '',
  });

  useEffect(() => {
    // eslint-disable-next-line
    if (!isNew) fetchCard();
  }, [id, isNew]); // eslint-disable-line

  const fetchCard = async () => {
    try {
      const { data } = await cardsAPI.get(id);
      setFormData(data);
      const [appleRes, googleRes] = await Promise.all([
        walletAPI.appleTemplate(id),
        walletAPI.googleTemplate(id),
      ]);
      setWalletTemplates({ apple: appleRes.data, google: googleRes.data });
    } catch (err) {
      setError('Impossible de charger la carte');
    } finally {
      setLoading(false);
    }
  };

  /* ── Autosave indicator ── */
  const triggerAutosave = () => {
    if (isNew) return;
    setSaveStatus('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      setSaveStatus('saved');
      saveTimerRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
    }, 800);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseInt(value) || 0 : value }));
    triggerAutosave();
  };

  /** Wrapper passed to child tabs — triggers autosave on every mutation */
  const setFormDataWithSave = (updater) => {
    setFormData(updater);
    triggerAutosave();
  };

  const handleColorPreset = (preset) => {
    setFormData(prev => ({ ...prev, primary_color: preset.primary, secondary_color: preset.secondary }));
    triggerAutosave();
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) { setError('Enregistrez la carte avant d\'importer un logo'); return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/cards/${id}/upload-logo`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, logo_url: data.logo_url }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'import du logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await cardsAPI.create(formData);
        navigate(`/dashboard/cards/${data.id}`);
      } else {
        await cardsAPI.update(id, formData);
        fetchCard();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const getLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  const visibleTabs = TABS.filter(t => !(t.requiresSave && isNew));
  const currentIndex = visibleTabs.findIndex(t => t.id === activeTab);
  const goNext = () => { if (currentIndex < visibleTabs.length - 1) setActiveTab(visibleTabs[currentIndex + 1].id); };
  const goPrev = () => { if (currentIndex > 0) setActiveTab(visibleTabs[currentIndex - 1].id); };
  const isLast = currentIndex === visibleTabs.length - 1;

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [activeTab]);

  /* ── Smart Stepper logic ── */
  const isTabAccessible = (tabIndex) => {
    if (!isNew) return true;
    for (let i = 0; i < tabIndex; i++) {
      if (OPTIONAL_TABS.has(visibleTabs[i].id)) continue;
      if (!isTabComplete(visibleTabs[i].id, formData)) return false;
    }
    return true;
  };

  const requiredTabs = visibleTabs.filter(t => !OPTIONAL_TABS.has(t.id));
  const completedCount = requiredTabs.filter(t => isTabComplete(t.id, formData)).length;
  const progressPercent = Math.round((completedCount / Math.max(1, requiredTabs.length)) * 100);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header + Autosave indicator ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard/cards')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Créer une carte de fidélité' : 'Modifier la carte'}
          </h1>
        </div>
        <div data-testid="autosave-indicator" className="h-6">
          {saveStatus === 'saving' && (
            <span className="flex items-center gap-1.5 text-sm text-slate-400 animate-pulse">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sauvegarde…
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="flex items-center gap-1.5 text-sm text-green-600">
              <Check className="w-3.5 h-3.5" /> Sauvegardé automatiquement
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── Smart Stepper ── */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between px-1" data-testid="editor-progress-bar">
          {visibleTabs.map((tab, i) => {
            const completed = isTabComplete(tab.id, formData);
            const isCurrent = tab.id === activeTab;
            const locked = !isTabAccessible(i);
            return (
              <React.Fragment key={tab.id}>
                <button
                  onClick={() => { if (!locked) setActiveTab(tab.id); }}
                  disabled={locked}
                  className={`flex flex-col items-center gap-1.5 transition-opacity ${locked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer group'}`}
                  data-testid={`progress-step-${tab.id}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200
                    ${completed && !isCurrent
                      ? 'bg-[#16A34A] text-white'
                      : isCurrent
                        ? 'bg-[#2563EB] text-white ring-[3px] ring-blue-200 shadow-lg shadow-blue-500/25'
                        : locked
                          ? 'bg-slate-100 text-slate-300 border border-slate-200'
                          : 'bg-slate-200 text-slate-500 group-hover:bg-slate-300'
                    }`}
                  >
                    {completed && !isCurrent
                      ? <Check className="w-4 h-4" />
                      : locked
                        ? <Lock className="w-3 h-3" />
                        : tab.num}
                  </div>
                  <span className={`text-xs font-medium transition-colors
                    ${isCurrent ? 'text-[#2563EB]' : locked ? 'text-slate-300' : completed ? 'text-[#16A34A]' : 'text-slate-500'}`}>
                    {tab.label}
                  </span>
                </button>
                {i < visibleTabs.length - 1 && (
                  <div className={`flex-1 h-[3px] mx-1 mt-[-18px] rounded-full transition-colors duration-300
                    ${completed || OPTIONAL_TABS.has(tab.id) ? 'bg-[#16A34A]' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Progress info bar */}
        <div className="flex items-center justify-between px-1">
          <span className="text-sm text-slate-500">
            Étape {currentIndex + 1} sur {visibleTabs.length}
            {OPTIONAL_TABS.has(activeTab) && <span className="text-slate-400"> (facultative)</span>}
          </span>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors
            ${progressPercent === 100
              ? 'bg-green-50 text-[#16A34A] border border-green-200'
              : 'bg-blue-50 text-[#2563EB] border border-blue-200'}`}
            data-testid="progress-percent"
          >
            {progressPercent === 100 ? 'Carte prête' : `${progressPercent}% complété`}
          </span>
        </div>
      </div>

      {/* ── Tab content ── */}
      <div>
        {activeTab === 'design' && (
          <DesignTab formData={formData} handleChange={handleChange} handleSubmit={handleSubmit} saving={saving} isNew={isNew} getLogoUrl={getLogoUrl} />
        )}
        {activeTab === 'background' && (
          <BackgroundTab formData={formData} setFormData={setFormDataWithSave} isNew={isNew} id={id} uploading={uploading} setUploading={setUploading} setError={setError} getLogoUrl={getLogoUrl} />
        )}
        {activeTab === 'colors' && (
          <ColorsTab formData={formData} handleChange={handleChange} handleColorPreset={handleColorPreset} handleLogoUpload={handleLogoUpload} handleSubmit={handleSubmit} saving={saving} isNew={isNew} uploading={uploading} getLogoUrl={getLogoUrl} />
        )}
        {activeTab === 'rewards' && (
          <RewardsTab formData={formData} setFormData={setFormDataWithSave} handleChange={handleChange} handleSubmit={handleSubmit} saving={saving} cardId={id} isNew={isNew} uploading={uploading} setUploading={setUploading} setError={setError} />
        )}
        {activeTab === 'wallet' && !isNew && (
          <WalletTab formData={formData} id={id} walletTemplates={walletTemplates} />
        )}
      </div>

      {/* ── Bottom navigation ── */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200" data-testid="editor-nav-bar">
        <div>
          {currentIndex > 0 && (
            <Button variant="outline" onClick={goPrev} data-testid="nav-prev-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Précédent
            </Button>
          )}
        </div>
        <div>
          {isLast ? (
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => navigate('/dashboard/cards')} data-testid="nav-finish-btn">
              <Eye className="w-4 h-4 mr-2" /> Carte prête — Voir mes cartes
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button className="bg-[#2563EB] hover:bg-blue-700" onClick={goNext} data-testid="nav-next-btn">
              Suivant <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardEditorPage;
